import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ResendConfirmationModal from './ResendConfirmationModal';
import AccountStatusModal from './AccountStatusModal';

const SessionHandler: React.FC = () => {
  const { user, checkSession, setShowPasswordRecoveryForm, setIsInRecoveryMode, setShowAuthModal } = useAuth();
  const { showSuccess, showError } = useToast();
  const processedRef = useRef(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [showAccountStatusModal, setShowAccountStatusModal] = useState(false);
  const [resendEmail, setResendEmail] = useState('');

  useEffect(() => {
    // Skip processing on OAuth callback routes - let AuthCallbackPage handle those
    if (window.location.pathname === '/auth/callback') {
      return;
    }
    
    // Prevent re-execution
    if (processedRef.current) {
      return;
    }

    const handleSessionFromURL = async () => {
      // Check if there's session data in the URL fragment (from email confirmation)
      const fragment = window.location.hash.substring(1);

      if (!fragment) {
        return;
      }

      const urlParams = new URLSearchParams(fragment);
      const accessToken = urlParams.get('access_token');
      const refreshToken = urlParams.get('refresh_token');
      const expiresIn = urlParams.get('expires_in');
      const type = urlParams.get('type');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      // Check if this is a password reset link (recovery type)
      // Enhanced detection for different password reset link formats
      const isPasswordResetLink = type === 'recovery' || 
        fragment.includes('type=recovery') ||
        window.location.pathname.includes('reset') ||
        window.location.search.includes('recovery') ||
        window.location.search.includes('reset') ||
        window.location.href.includes('recovery') ||
        window.location.href.includes('reset') ||
        // Only treat as password reset if it's explicitly recovery-related, not just any expired link
        (error === 'access_denied' && errorDescription?.includes('Email link is invalid or has expired') && 
         (type === 'recovery' || fragment.includes('type=recovery') || window.location.href.includes('recovery'))) ||
        (error && (errorDescription?.includes('password') || errorDescription?.includes('reset')));


      // Handle explicit errors in URL (expired/invalid tokens)
      if (error) {
        console.error('Session error from URL:', error, errorDescription);
        
        if (isPasswordResetLink) {
          // Show forgot password modal with specific error message instead of toast
          setShowAuthModal(
            true,
            'signin',
            'This password reset link has expired or is no longer valid. Please request a new password reset using the form below.'
          );
        } else if (error === 'access_denied' && errorDescription?.includes('Email link is invalid or has expired')) {
          // Handle expired signup confirmation links specifically
          setShowAccountStatusModal(true);
        } else {
          showError(
            'The link you clicked is no longer valid. Please try again.',
            { duration: 6000 }
          );
        }
        
        // Clean up the URL
        const cleanUrl = window.location.pathname + window.location.search;
        window.history.replaceState({}, document.title, cleanUrl);
        return;
      }

      // Handle missing tokens (reused/expired links)
      if (!accessToken || !refreshToken) {
        // If we have a fragment but no tokens, this is likely a reused/expired link
        if (fragment && (isPasswordResetLink || type)) {

          if (isPasswordResetLink || type === 'recovery') {
  // Show forgot password modal with specific error message instead of toast
            setShowAuthModal(
              true, 
              'signin', 
              'This password reset link can only be used once and has already been processed. Please request a new password reset using the form below.'
            );
          } else if (type === 'signup') {
            showError(
              'This account confirmation link has already been used or has expired.',
              { duration: 6000 }
            );
          } else {
            showError(
              'This link has already been used or has expired. Please try again.',
              { duration: 6000 }
            );
          }
          
          // Clean up the URL
          const cleanUrl = window.location.pathname + window.location.search;
          window.history.replaceState({}, document.title, cleanUrl);
        }
        return;
      }

      // Only process if we have session tokens and a valid type
      if (!type) {
        // We have tokens but no type - this shouldn't happen with valid Supabase URLs
        showError(
          'Invalid link format. Please make sure you clicked the correct link from the email.',
          { duration: 6000 }
        );
        
        // Clean up the URL
        const cleanUrl = window.location.pathname + window.location.search;
        window.history.replaceState({}, document.title, cleanUrl);
        return;
      }

      processedRef.current = true;

      try {

        // Store the session based on type (email confirmations usually want to be remembered)
        const session = {
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_at: Date.now() + (parseInt(expiresIn || '3600') * 1000)
        };

        // Store in localStorage for email confirmations (users likely want to stay logged in)
        localStorage.setItem('cycling-buddy-session', JSON.stringify(session));

        // Trigger auth context update
        await checkSession();

        // Show appropriate success message based on type
        let successMessage = 'Welcome! You have been logged in successfully.';
        
        if (type === 'signup') {
          successMessage = 'Your account has been activated! Welcome to CyclingBuddy!';
        } else if (type === 'recovery') {
          // For password recovery, first check if user is already logged in
          if (user) {
            showError(
              'You are already logged in. If you want to change your password, please use the settings page or log out first.',
              { duration: 6000 }
            );
            
            // Clean up the URL
            const cleanUrl = window.location.pathname + window.location.search;
            window.history.replaceState({}, document.title, cleanUrl);
            return;
          }
          
       
          // Store recovery session separately (not as main session)
          const recoverySession = {
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_at: Date.now() + (parseInt(expiresIn || '3600') * 1000),
            type: 'recovery'
          };
          localStorage.setItem('cycling-buddy-recovery-session', JSON.stringify(recoverySession));
          
          // Set recovery mode flags
          setIsInRecoveryMode(true);
          
          // Don't store regular session or call checkSession yet
          // Show password reset form without logging in
          setShowPasswordRecoveryForm(true);
          
          // Don't show success message yet, wait until after password is reset
          return;
        } else if (type === 'email_change') {
          successMessage = 'Email change confirmed successfully!';
        }

        // Only show success message if not recovery (recovery will show form instead)
        if (type !== 'recovery') {
          showSuccess(successMessage);
        }

        // Clean up the URL by removing the fragment
        const cleanUrl = window.location.pathname + window.location.search;
        window.history.replaceState({}, document.title, cleanUrl);

       } catch (err) {
        console.error('Error processing session from URL:', err);
        
        // Show user-friendly error message based on context
        if (type === 'recovery') {
          // Show forgot password modal with specific error message instead of toast
          setShowAuthModal(
            true,
            'signin',
            'There was a problem processing your password reset link. This usually means the link has expired or already been used. Please request a new password reset using the form below.'
          );
        } else if (type === 'signup') {
          showError(
            'There was a problem confirming your account. Please check your email or try signing up again.',
            { duration: 6000 }
          );
        } else {
          showError(
            'There was a problem processing the link. Please try again.',
            { duration: 6000 }
          );
        }
        
        // Clean up the URL even on error
        const cleanUrl = window.location.pathname + window.location.search;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    };

    handleSessionFromURL();
  }, []); // Empty dependency array - this should only run once on mount

  // Handle custom event from AccountStatusModal to show ResendConfirmationModal
  useEffect(() => {
    const handleShowResendModal = (event: any) => {
      const { email } = event.detail;
      setResendEmail(email);
      setShowResendModal(true);
    };

    window.addEventListener('showResendModal', handleShowResendModal);

    return () => {
      window.removeEventListener('showResendModal', handleShowResendModal);
    };
  }, []);

  return (
    <>
      <AccountStatusModal
        open={showAccountStatusModal}
        onClose={() => setShowAccountStatusModal(false)}
      />
      <ResendConfirmationModal
        open={showResendModal}
        onClose={() => {
          setShowResendModal(false);
          setResendEmail('');
        }}
        initialEmail={resendEmail}
        reason="unconfirmed"
      />
    </>
  );
};

export default SessionHandler;
